const STORAGE_KEY = "wordhit_scores";

const defaultScores = {
  score: 0,
  streak: 0,
  maxStreak: 0,
  numbersPlayed: 0,
  lastPlayedId: null,
  lastWonId: null,
  playedMonthOdd: 0,
  playedMonthEven: 0,
};

function canUseStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function readStorage() {
  if (!canUseStorage()) {
    return { ...defaultScores };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { ...defaultScores };
    }
    const parsed = JSON.parse(raw);
    return {
      score: Number.isFinite(parsed?.score) ? parsed.score : 0,
      streak: Number.isFinite(parsed?.streak) ? parsed.streak : 0,
      maxStreak: Number.isFinite(parsed?.maxStreak) ? parsed.maxStreak : 0,
      numbersPlayed:
        Number.isFinite(parsed?.numbersPlayed) ? parsed.numbersPlayed : 0,
      lastPlayedId:
        Number.isFinite(parsed?.lastPlayedId) || parsed?.lastPlayedId === null
          ? parsed.lastPlayedId
          : null,
      lastWonId:
        Number.isFinite(parsed?.lastWonId) || parsed?.lastWonId === null
          ? parsed.lastWonId
          : null,
      playedMonthOdd: Number.isFinite(parsed?.playedMonthOdd) ? parsed.playedMonthOdd : 0,
      playedMonthEven: Number.isFinite(parsed?.playedMonthEven) ? parsed.playedMonthEven : 0,
    };
  } catch (error) {
    console.warn("Unable to read score storage", error);
    return { ...defaultScores };
  }
}

function writeStorage(data) {
  if (!canUseStorage()) {
    return;
  }
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        score: data.score,
        streak: data.streak,
        maxStreak: data.maxStreak,
        numbersPlayed: data.numbersPlayed,
        lastPlayedId: data.lastPlayedId ?? null,
        lastWonId: data.lastWonId ?? null,
        playedMonthOdd: data.playedMonthOdd ?? 0,
        playedMonthEven: data.playedMonthEven ?? 0,
      })
    );
  } catch (error) {
    console.warn("Unable to persist score storage", error);
  }
}

export function getStoredScores() {
  return readStorage();
}

export function updateScores({ idNumber, clicksLeft, wordsFound }) {
  const stored = readStorage();

  const normalizedWordsFound = Number.isFinite(wordsFound) ? wordsFound : Number(wordsFound) || 0;
  const normalizedClicksLeft = Number.isFinite(clicksLeft) ? clicksLeft : Number(clicksLeft) || 0;
  const normalizedId = Number.isFinite(idNumber) ? idNumber : Number(idNumber);
  const hasValidId = Number.isFinite(normalizedId);

  const isWin = normalizedWordsFound >= 3;

  let streak = stored.streak || 0;
  let maxStreak = stored.maxStreak || 0;
  let numbersPlayed = stored.numbersPlayed || 0;
  let lastPlayedId = stored.lastPlayedId;
  let lastWonId = stored.lastWonId;
  let playedMonthOdd = stored.playedMonthOdd || 0;
  let playedMonthEven = stored.playedMonthEven || 0;

  if (hasValidId) {
    // Always record that this id was played (win or loss)
    if (
      typeof stored.lastPlayedId !== "number" ||
      normalizedId > stored.lastPlayedId
    ) {
      numbersPlayed += 1;
      
      // NOTE: playedMonthOdd/playedMonthEven are NOT incremented here
      // They are incremented on the server in saveUserScores when incrementPlayedCount is true
      // Local storage just preserves the values from the server
    }
    lastPlayedId = normalizedId;

    // Streak continuity is based on last day you WON, not last day you played
    if (isWin) {
      if (typeof lastWonId === "number") {
        const diffFromLastWin = normalizedId - lastWonId;
        if (diffFromLastWin === 1) {
          streak = (stored.streak || 0) + 1;
        } else if (diffFromLastWin === 0) {
          // Same day replay with a win -> keep current streak
          streak = stored.streak || 0;
        } else if (diffFromLastWin > 1) {
          // Gap since last win -> start again at 1
          streak = 1;
        } else {
          // Playing an older puzzle than the last win -> don't change streak
          streak = stored.streak || 0;
        }
      } else {
        // First ever win
        streak = 1;
      }
      lastWonId = normalizedId;
    } else {
      // Loss breaks the streak
      streak = 0;
      // lastWonId unchanged on loss
    }

    // Update maxStreak after computing new streak
    maxStreak = Math.max(maxStreak, streak);
  } else {
    // Invalid idNumber - log warning but don't throw
    console.warn("updateScores called with invalid idNumber:", idNumber, "isWin:", isWin);
  }

  let pointsEarned = 0;
  if (isWin) {
    pointsEarned = Math.max(0, normalizedClicksLeft);
  }
  const score = (stored.score || 0) + pointsEarned;

  // Automatic detection possibleCheater
  // Detect if user gets perfect game (10 points = 10 clicks left) within 10 games
  // Perfect game = 10 punten = 10 clicks over (ongeacht hoeveel woorden gevonden)
  const isPerfectGame = isWin && normalizedClicksLeft === 10;
  
  console.log("[PossibleCheater] Detection in updateScores:", {
    isWin,
    wordsFound: normalizedWordsFound,
    clicksLeft: normalizedClicksLeft,
    pointsEarned,
    isPerfectGame,
    idNumber: normalizedId,
    explanation: isPerfectGame ? "Perfect game: 10 clicks left = 10 points" : "Not perfect game",
  });
  
  if (isPerfectGame && hasValidId) {
    const firstPerfectGameId = localStorage.getItem("firstPerfectGameId");
    
    if (!firstPerfectGameId) {
      // First perfect game: save idNumber
      localStorage.setItem("firstPerfectGameId", String(normalizedId));
      const triggerIdNumber = normalizedId + 10;
      localStorage.setItem("triggerIdNumber", String(triggerIdNumber));
      
      console.log("[PossibleCheater] First perfect game detected:", {
        firstPerfectGameId: normalizedId,
        triggerIdNumber,
        wordsFound: normalizedWordsFound,
        clicksLeft: normalizedClicksLeft,
      });
    } else {
      // Second perfect game: check if within 10 games
      const firstId = parseInt(firstPerfectGameId, 10);
      const triggerIdNumber = parseInt(localStorage.getItem("triggerIdNumber") || "0", 10);
      
      console.log("[PossibleCheater] Perfect game detected:", {
        firstPerfectGameId: firstId,
        currentIdNumber: normalizedId,
        triggerIdNumber,
        wordsFound: normalizedWordsFound,
        clicksLeft: normalizedClicksLeft,
      });
      
      // Check if current perfect game is within trigger window (<= triggerIdNumber)
      if (normalizedId <= triggerIdNumber) {
        // Second perfect game within 10 games -> flag as possible cheater
        localStorage.setItem("shouldSetPossibleCheater", "true");
        console.log("[PossibleCheater] ⚠️ Flagged for possibleCheater: 2 perfect games within 10 games", {
          firstPerfectGameId: firstId,
          secondPerfectGameId: normalizedId,
          triggerIdNumber,
          gamesBetween: normalizedId - firstId,
        });
      } else {
        // Perfect game is outside trigger window, reset and start new tracking
        localStorage.setItem("firstPerfectGameId", String(normalizedId));
        const newTriggerIdNumber = normalizedId + 10;
        localStorage.setItem("triggerIdNumber", String(newTriggerIdNumber));
        console.log("[PossibleCheater] Perfect game outside trigger window, resetting tracking:", {
          newFirstPerfectGameId: normalizedId,
          newTriggerIdNumber,
          previousFirstId: firstId,
        });
      }
    }
  } else if (!isPerfectGame && hasValidId) {
    // Not a perfect game: check if we should clear the tracking
    // Only clear if we're past the trigger window
    const triggerIdNumber = parseInt(localStorage.getItem("triggerIdNumber") || "0", 10);
    if (triggerIdNumber > 0 && normalizedId > triggerIdNumber) {
      // We're past the trigger window, clear tracking
      console.log("[PossibleCheater] Past trigger window, clearing tracking:", {
        currentIdNumber: normalizedId,
        triggerIdNumber,
        isWin,
        clicksLeft: normalizedClicksLeft,
      });
      localStorage.removeItem("firstPerfectGameId");
      localStorage.removeItem("triggerIdNumber");
      localStorage.removeItem("shouldSetPossibleCheater");
    }
  }

  const updated = {
    score,
    streak,
    maxStreak,
    numbersPlayed,
    lastPlayedId,
    lastWonId,
    playedMonthOdd,
    playedMonthEven,
  };

  writeStorage(updated);

  return {
    score,
    streak,
    maxStreak,
    numbersPlayed,
    pointsEarned,
    lastWonId, // Include lastWonId in return value so it can be saved to Parse
  };
}

/**
 * Clicks (daily) or word points (battle) for one guess.
 * One newly completed board word awards 1. Two or more award 2.
 * A dictionary guess that completes nothing costs 1 click in daily.
 * Battle has no click pool, so it uses Math.max(0, rewardForGuess(...)).
 */
export function rewardForGuess(newlyCompletedCount, onGrid) {
  const count = Number.isFinite(newlyCompletedCount) ? newlyCompletedCount : 0;
  if (!onGrid && count <= 0) return -1;
  return count > 1 ? 2 : 1;
}

export function overwriteStoredScores({ score, streak, maxStreak, numbersPlayed, lastPlayedId, lastWonId, playedMonthOdd, playedMonthEven }) {
  const merged = {
    ...defaultScores,
    score: Number.isFinite(score) ? score : defaultScores.score,
    streak: Number.isFinite(streak) ? streak : defaultScores.streak,
    maxStreak: Number.isFinite(maxStreak) ? maxStreak : defaultScores.maxStreak,
    numbersPlayed: Number.isFinite(numbersPlayed) ? numbersPlayed : defaultScores.numbersPlayed,
    lastPlayedId:
      Number.isFinite(lastPlayedId) || lastPlayedId === null
        ? lastPlayedId
        : defaultScores.lastPlayedId,
    lastWonId:
      Number.isFinite(lastWonId) || lastWonId === null
        ? lastWonId
        : defaultScores.lastWonId,
    playedMonthOdd: Number.isFinite(playedMonthOdd) ? playedMonthOdd : defaultScores.playedMonthOdd,
    playedMonthEven: Number.isFinite(playedMonthEven) ? playedMonthEven : defaultScores.playedMonthEven,
  };

  writeStorage(merged);
}

